/**
 * googleCalendar.repository — 직원별 Google 캘린더 연결 (설계 docs/design/11_Google캘린더_연결설계.md).
 *
 * 읽기(본인만, rules):  users/{uid}/google_link/state · users/{uid}/google_events
 * 쓰기: 클라이언트는 사본을 직접 쓰지 않는다. google_calendar_ops/{autoId} 요청 문서를 만들면
 *   CF googleCalendarOp 가 Google 에 반영하고 사본을 고친 뒤 status done/error 를 적는다(admin_ops 와 같은 트리거 패턴).
 * 실시간 목록은 orderBy 를 쓰지 않는다 → 정렬은 domain sortGoogleEvents.
 */
import { addDoc, collection, doc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";
import {
  coerceGoogleEvent,
  coerceLinkState,
  type GoogleCalendarOpAction,
  type GoogleEvent,
  type GoogleLinkState,
} from "@/domain/googleCalendar";

type ErrCb = (e: Error & { code?: string }) => void;

/** 연결·첫 동기화는 Google 조회가 여러 번이라 넉넉히 */
const OP_TIMEOUT_MS = 90_000;

export const googleCalendarRepo = {
  watchState(uid: string, cb: (s: GoogleLinkState | null) => void, onError?: ErrCb): () => void {
    return onSnapshot(
      doc(db, "users", uid, "google_link", "state"),
      (snap) => cb(coerceLinkState(snap.data())),
      onError,
    );
  },

  watchEvents(uid: string, cb: (list: GoogleEvent[]) => void, onError?: ErrCb): () => void {
    return onSnapshot(
      collection(db, "users", uid, "google_events"),
      (snap) =>
        cb(
          snap.docs
            .map((d) => coerceGoogleEvent(d.id, d.data()))
            .filter((e): e is GoogleEvent => e !== null),
        ),
      onError,
    );
  },

  /** 요청 1건 실행 — 서버 result 를 돌려주고, 실패면 서버가 적은 문장으로 throw */
  runOp<T = unknown>(uid: string, action: GoogleCalendarOpAction, params: Record<string, unknown> = {}): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      let settled = false;
      let unsub: (() => void) | null = null;
      let timer = 0;
      const finish = (fn: () => void) => {
        if (settled) return;
        settled = true;
        if (unsub) unsub();
        if (timer) window.clearTimeout(timer);
        fn();
      };

      addDoc(collection(db, "google_calendar_ops"), {
        uid,
        action,
        params,
        status: "pending",
        created_at: serverTimestamp(),
      })
        .then((ref) => {
          timer = window.setTimeout(
            () => finish(() => reject(new Error("Google 응답이 늦습니다. 잠시 뒤 새로고침하세요."))),
            OP_TIMEOUT_MS,
          );
          unsub = onSnapshot(
            ref,
            (snap) => {
              const d = snap.data();
              if (!d) return;
              if (d.status === "done") finish(() => resolve((d.result ?? null) as T));
              else if (d.status === "error") finish(() => reject(new Error((d.error as string) || "처리하지 못했습니다.")));
            },
            (e) => finish(() => reject(e)),
          );
        })
        .catch((e) => finish(() => reject(e)));
    });
  },
};
